// The step-up dialog (#636, D10): the mock's "Confirm it is you", drawn as this
// PR's screen.
//
// D10 lands passkeys as a second factor and a step-up method, and the mock's
// approval is a passkey step-up bound to the change (`end-state-mockup.html`,
// screens 4 and 5: "passkey step-up bound to CHG-0912"). This is that dialog:
// **the passkey is offered first when one is enrolled**, with the authenticator
// app and the password as the fallbacks a session without one uses.
//
// **The proof the card is handed is derived from one state machine**, not from
// whichever field was touched last. `mode` is the method; typing a password
// selects password, choosing the authenticator app selects TOTP, and either
// clears a passkey proof that has been superseded. `stepUpProof` is the only
// thing reported upward, so `/auth/step-up` is never handed a credential that
// belongs to a method the person did not choose.
//
// **The dialog owns no authorization.** It collects a `StepUpProof` — a method
// and the credential to prove it with — and hands it to the card, which posts it
// to `/auth/step-up` for the purpose named here. A proof for one purpose opens no
// other; the server reads the binding, not this file's copy of it.
//
// **It offers only the factors the user holds** (#659): the passkey, the
// authenticator app, a recovery code (#729) and the password are each drawn only
// when `factors` says the user has one, and the reducer refuses to choose a method that is not held, so
// a hidden button cannot still report its proof. `factors` is the caller's
// reading of `GET /auth/mfa`, not this file's. **Until it answers, nothing is
// offered** (#739): the dialog says it is reading, and a failed reading offers
// to read again; it never guesses a factor.
//
// The passkey ceremony is real browser WebAuthn (`navigator.credentials.get`),
// shared with the login challenge through `./passkey`.

import { useCallback, useEffect, useReducer, useRef, useState, type ReactNode } from "react";
import { flushSync } from "react-dom";

import type { PasskeyChallenge, StepUpFactors } from "../api/passkeys";
import { passkeyCredential } from "./passkey";
import { Field, Notice } from "./chrome";

export type { StepUpFactors } from "../api/passkeys";

/** No factor held: what the state machine holds while `GET /auth/mfa` has not answered. */
export const NO_FACTORS: StepUpFactors = {
  passkey: false,
  totp: false,
  password: false,
  recovery: false,
};

/**
 * `GET /auth/mfa` did not answer; `retry` reads it again (#739), or is `null`
 * when the site has no reader and there is nothing to read again (#741).
 */
export interface FactorsUnread {
  retry: (() => void) | null;
}

/**
 * A site's reading of `GET /auth/mfa`: the factors held, a failed reading, or
 * `undefined` while it is being read (#739).
 */
export type FactorsReading = StepUpFactors | FactorsUnread | undefined;

/** The factors a reading holds, or `null` while there is no answer to offer. */
export const heldFactors = (reading: FactorsReading): StepUpFactors | null =>
  reading === undefined || "retry" in reading ? null : reading;

/** What is proved at a step-up site: a method and its credential. */
export interface StepUpProof {
  method: string;
  credential: string;
}

/** Mint an assertion challenge for one purpose. The console supplies this. */
export type BeginPasskey = (purpose: string) => Promise<PasskeyChallenge>;

export type StepUpMode = "passkey" | "totp" | "recovery" | "password";

export interface StepUpState {
  /** What the user holds; a method not held is never chosen. */
  factors: StepUpFactors;
  mode: StepUpMode;
  password: string;
  code: string;
  /** A recovery code, typed (#729). */
  recovery: string;
  /** The assertion JSON a completed passkey ceremony produced, or null. */
  passkey: string | null;
}

export type StepUpAction =
  | { type: "choose"; mode: "totp" | "recovery" | "password" }
  | { type: "password"; value: string }
  | { type: "code"; value: string }
  | { type: "recovery"; value: string }
  | { type: "passkey"; credential: string }
  /** A new reading of what the user holds (`GET /auth/mfa` answering late). */
  | { type: "factors"; factors: StepUpFactors };

/**
 * The method a site opens on: the passkey first, then the password, then the
 * app; a recovery code is the fallback a person chooses.
 */
function firstMode(factors: StepUpFactors): StepUpMode {
  if (factors.passkey) return "passkey";
  if (factors.password) return "password";
  if (factors.totp) return "totp";
  if (factors.recovery === true) return "recovery";
  return "password";
}

const holds = (factors: StepUpFactors, mode: StepUpMode): boolean =>
  mode === "passkey" ? factors.passkey
  : mode === "totp" ? factors.totp
  : mode === "recovery" ? factors.recovery === true
  : factors.password;

export function initialStepUpState(factors: StepUpFactors): StepUpState {
  return {
    factors,
    mode: firstMode(factors),
    password: "",
    code: "",
    recovery: "",
    passkey: null,
  };
}

export function stepUpReducer(state: StepUpState, action: StepUpAction): StepUpState {
  if (action.type === "factors") return refactored(state, action.factors);
  // A method the user does not hold is not a choice, however it was asked for.
  const mode =
    action.type === "choose" ? action.mode
    : action.type === "password" ? "password"
    : action.type === "code" ? "totp"
    : action.type === "recovery" ? "recovery"
    : "passkey";
  if (!holds(state.factors, mode)) return state;
  switch (action.type) {
    case "choose":
      // A fallback chosen after a passkey proof supersedes that proof.
      return { ...state, mode: action.mode, passkey: null };
    case "password":
      // Typing a password is choosing password, even while a passkey is offered.
      return { ...state, mode: "password", password: action.value, passkey: null };
    case "code":
      return { ...state, mode: "totp", code: action.value, passkey: null };
    case "recovery":
      return { ...state, mode: "recovery", recovery: action.value, passkey: null };
    case "passkey":
      return { ...state, mode: "passkey", passkey: action.credential };
  }
}

/**
 * The same dialog under a new reading of the factors. What the person already
 * typed or proved is kept while its method is still held, so a reading that
 * lands after they started typing does not wipe it; an untouched dialog moves
 * to the new first method, as it would have opened on it.
 */
function refactored(state: StepUpState, factors: StepUpFactors): StepUpState {
  const kept: StepUpState = {
    factors,
    mode: state.mode,
    password: factors.password ? state.password : "",
    code: factors.totp ? state.code : "",
    recovery: factors.recovery === true ? state.recovery : "",
    passkey: factors.passkey ? state.passkey : null,
  };
  const touched =
    (state.mode === "password" && kept.password !== "") ||
    (state.mode === "totp" && kept.code !== "") ||
    (state.mode === "recovery" && kept.recovery !== "") ||
    (state.mode === "passkey" && kept.passkey !== null);
  return { ...kept, mode: touched && holds(factors, state.mode) ? state.mode : firstMode(factors) };
}

/** The proof this state offers the card, or `null` while none is chosen. */
export function stepUpProof(state: StepUpState): StepUpProof | null {
  if (!holds(state.factors, state.mode)) return null;
  if (state.mode === "passkey") {
    return state.passkey === null ? null : { method: "passkey", credential: state.passkey };
  }
  if (state.mode === "totp") return { method: "totp", credential: state.code };
  if (state.mode === "recovery") return { method: "recovery", credential: state.recovery };
  return { method: "password", credential: state.password };
}

/**
 * The dialog. `factors` decides what is offered and in which order, and while
 * it is unread nothing is (#739); `beginPasskey` is what makes the passkey
 * button do anything, optional so a server-rendered test needs no browser API.
 */
interface StepUpDialogProps {
  purpose: string;
  /** The change this proof is bound to, named on the dialog as the mock names CHG-0912. */
  changeId?: string | null;
  /** What the user holds, from `GET /auth/mfa`; unread, nothing is offered. */
  factors: FactorsReading;
  disabled?: boolean;
  beginPasskey?: BeginPasskey;
  /** The proof now on offer; `null` while a passkey ceremony has not completed. */
  onProofChange: (proof: StepUpProof | null) => void;
  onError?: (message: string) => void;
  /**
   * `group` where the check is drawn inside another dialog, or in place beside the act
   * it gates, so a modal drawer holds one dialog and not two (#809).
   */
  role?: "dialog" | "group";
  /**
   * What the proof is bound to, as the person reads it (#809: "this change alone"). The
   * purpose's own code is named instead when this is not given.
   */
  boundTo?: string;
  /** Focus moves into the check when it is drawn: it was opened by the act it gates (#809). */
  autoFocus?: boolean;
  /** The act's own controls, drawn inside the check's frame after the factors. */
  children?: ReactNode;
}

/**
 * The acts that add or remove a factor, a device approval, which mints a
 * standing credential, and a request to delete the account: the server refuses
 * a recovery code for them (#737, #743, #1136), so the dialog does not offer one.
 */
export const RECOVERY_REFUSED_PURPOSES: readonly string[] = [
  "passkey_register",
  "passkey_remove",
  "mfa_setup",
  "mfa_disable",
  "device_approve",
  "account_delete",
];

/**
 * The acts the server refuses a grant the password proved for (#746): replacing a
 * lost authenticator needs a factor, so the dialog does not offer the password.
 */
export const PASSWORD_REFUSED_PURPOSES: readonly string[] = ["mfa_replace"];

/** What a purpose may be proven with, of what the user holds. */
const offeredFor = (purpose: string, held: StepUpFactors): StepUpFactors => ({
  ...held,
  recovery: RECOVERY_REFUSED_PURPOSES.includes(purpose) ? false : held.recovery,
  password: PASSWORD_REFUSED_PURPOSES.includes(purpose) ? false : held.password,
});

export function StepUpDialog({
  purpose,
  changeId = null,
  factors: reading,
  disabled = false,
  beginPasskey,
  onProofChange,
  onError,
  role = "dialog",
  boundTo,
  autoFocus = false,
  children,
}: StepUpDialogProps) {
  const read = heldFactors(reading);
  // Filtered once the reading has resolved, so the reading and failed states stand.
  const factors: StepUpFactors = read === null ? NO_FACTORS : offeredFor(purpose, read);
  const [state, send] = useReducer(stepUpReducer, factors, initialStepUpState);
  // A new reading of the factors reaches the state machine without remounting
  // it, so what the person typed before it landed is kept. It reaches it in the
  // same render that draws the new buttons, not in an effect after it: a button
  // pressed in between would ask a state machine that has not yet heard of the
  // factor, and be refused (#729).
  const { passkey: holdsPasskey, totp: holdsTotp, password: holdsPassword } = factors;
  const holdsRecovery = factors.recovery === true;
  const answer = [holdsPasskey, holdsTotp, holdsPassword, holdsRecovery].join();
  const [heard, setHeard] = useState(answer);
  if (heard !== answer) {
    setHeard(answer);
    send({
      type: "factors",
      factors: {
        passkey: holdsPasskey,
        totp: holdsTotp,
        password: holdsPassword,
        recovery: holdsRecovery,
      },
    });
  }
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const reported = useRef(onProofChange);
  reported.current = onProofChange;
  const frame = useRef<HTMLElement | null>(null);
  const focused = useRef(false);
  const offersPasskey = factors.passkey;
  // Where the reading of the factors is: the frame's content is redrawn at each step.
  const stage = read !== null ? "ready" : reading !== undefined && "retry" in reading ? "failed" : "reading";
  // Into the check when it opens, onto the method it opens on: the passkey when one is
  // offered, else the field to type in. Until the factors are read there is no such
  // control, so focus waits on the frame, never on a control the reading replaces (#809);
  // it moves on when they land, unless the person has moved on first. Focus dropped to
  // the page by a control going away (Try again, pressed) is brought back.
  useEffect(() => {
    const element = frame.current;
    if (!autoFocus || element === null) return;
    const at = document.activeElement;
    const lost = at === null || at === document.body;
    if (focused.current && at !== element && !lost) return;
    focused.current = true;
    if (stage !== "ready") {
      element.focus();
      return;
    }
    const control =
      element.querySelector<HTMLElement>(
        offersPasskey ? "button:not([disabled])" : "input:not([disabled])",
      ) ?? element.querySelector<HTMLElement>("input:not([disabled]), button:not([disabled])");
    (control ?? element).focus();
  }, [autoFocus, stage, offersPasskey]);

  // Report exactly when the effective proof changes, so a superseded passkey
  // proof cannot stay selected behind the authenticator app or the password.
  const proof = stepUpProof(state);
  const method = proof?.method ?? null;
  const credential = proof?.credential ?? null;
  useEffect(() => {
    reported.current(method === null ? null : { method, credential: credential ?? "" });
  }, [method, credential]);

  // A typed credential, or a method chosen, is reported by the handler that received it,
  // and the site is drawn with it before the handler returns, so an act pressed in the
  // same task sends what is now in the field, not the value one change earlier (#928),
  // and never the proof of the method just left (#942). React draws a controlled field's
  // change at the end of its event by itself, but not a button's click, hence
  // `flushSync`. The effect above reports what arrives outside a handler: a passkey
  // ceremony, a new reading of the factors.
  const typed = (action: Extract<StepUpAction, { type: "choose" } | { value: string }>) => {
    flushSync(() => {
      send(action);
      reported.current(stepUpProof(stepUpReducer(state, action)));
    });
  };

  const fail = (message: string) => {
    setProblem(message);
    onError?.(message);
  };

  const usePasskey = async () => {
    if (!beginPasskey) {
      fail("This browser cannot use a passkey yet.");
      return;
    }
    setProblem(null);
    setBusy(true);
    try {
      const began = await beginPasskey(purpose);
      const credential = await passkeyCredential(began);
      if (credential === null) {
        fail("The passkey prompt was dismissed. Try again or use another factor.");
        return;
      }
      send({ type: "passkey", credential });
    } catch (error) {
      fail(
        error instanceof Error
          ? error.message
          : "The passkey prompt did not complete. Try again or use another factor.",
      );
    } finally {
      setBusy(false);
    }
  };

  const suffix = changeId ?? purpose;
  if (read === null) {
    const failed = reading !== undefined && "retry" in reading ? reading : null;
    const retry = failed?.retry ?? null;
    return (
      <section
        ref={frame}
        className="step-up"
        role={role}
        aria-label="Confirm it is you"
        data-testid="step-up"
        tabIndex={autoFocus ? -1 : undefined}
      >
        <h3>Confirm it is you</h3>
        {failed === null ? (
          <p role="status" className="muted">
            Checking how you can confirm it is you: reading…
          </p>
        ) : (
          <>
            <Notice tone="bad">
              We could not read how you can confirm it is you, so nothing is offered yet.
            </Notice>
            {retry === null ? null : (
              <div className="actions">
                <button type="button" className="btn" onClick={retry}>
                  Try again
                </button>
              </div>
            )}
          </>
        )}
        {children}
      </section>
    );
  }
  // How many methods there are to choose between; the choice buttons are drawn
  // only when there is a choice.
  const offered = [factors.passkey, factors.totp, holdsRecovery, factors.password].filter(
    Boolean,
  ).length;
  return (
    <section
      ref={frame}
      className="step-up"
      role={role}
      aria-label={factors.passkey ? "Confirm with your passkey" : "Confirm it is you"}
      data-testid="step-up"
      tabIndex={autoFocus ? -1 : undefined}
    >
      <h3>Confirm it is you</h3>
      <p>
        This needs a fresh factor, even inside a signed-in session. It is bound to this one action,
        and cannot be reused for another.
      </p>
      {problem === null ? null : <Notice tone="bad">{problem}</Notice>}
      {factors.passkey ? (
        <div className="actions">
          <button
            type="button"
            className="btn btn--primary"
            disabled={disabled || busy}
            onClick={() => void usePasskey()}
          >
            {busy ? "Waiting for your passkey…" : "Use your passkey"}
          </button>
        </div>
      ) : null}
      {offered < 2 ? null : (
        <div className="actions">
          {factors.totp ? (
            <button
              type="button"
              className="btn"
              disabled={disabled || busy}
              onClick={() => typed({ type: "choose", mode: "totp" })}
            >
              Use authenticator app
            </button>
          ) : null}
          {holdsRecovery ? (
            <button
              type="button"
              className="btn"
              disabled={disabled || busy}
              onClick={() => typed({ type: "choose", mode: "recovery" })}
            >
              Use a recovery code
            </button>
          ) : null}
          {factors.password ? (
            <button
              type="button"
              className="btn"
              disabled={disabled || busy}
              onClick={() => typed({ type: "choose", mode: "password" })}
            >
              Use your password
            </button>
          ) : null}
        </div>
      )}
      {offered === 0 ? (
        <Notice tone="bad">
          You have no password, authenticator app or passkey to confirm this with.
        </Notice>
      ) : null}
      {state.mode === "totp" && factors.totp ? (
        <Field
          id={`step-up-${suffix}-code`}
          label="Your authenticator app's one-time code"
          inputMode="numeric"
          autoComplete="one-time-code"
          disabled={disabled || busy}
          value={state.code}
          onChange={(event) => typed({ type: "code", value: event.target.value })}
        />
      ) : null}
      {state.mode === "recovery" && holdsRecovery ? (
        <Field
          id={`step-up-${suffix}-recovery`}
          label="One of your recovery codes"
          autoComplete="off"
          disabled={disabled || busy}
          value={state.recovery}
          onChange={(event) => typed({ type: "recovery", value: event.target.value })}
        />
      ) : null}
      {factors.password ? (
      <Field
        id={`step-up-${suffix}-password`}
        label="Or your password, to prove it is you"
        type="password"
        autoComplete="current-password"
        disabled={disabled || busy}
        value={state.password}
        onChange={(event) => typed({ type: "password", value: event.target.value })}
      />
      ) : null}
      <small className="field__hint">
        Bound to {boundTo ?? <code>{purpose}</code>}.
      </small>
      {children}
    </section>
  );
}

/** What a site with no reader holds: a reading that failed, with nothing to retry (#741). */
const NO_READER: FactorsUnread = { retry: null };

/**
 * `GET /auth/mfa`'s factors, read for a screen (#659, #729). `undefined` while
 * it is read, and a `retry` when it fails, which reads it again (#739). No
 * outcome stands in for a factor.
 *
 * Every site's prop for the reader is required, but a caller whose types were
 * bypassed can still hand `undefined`: that is a failed reading with no retry,
 * and a warning in development, rather than "reading…" for good (#741).
 */
export function useStepUpFactors(
  read: (() => Promise<StepUpFactors>) | undefined,
): FactorsReading {
  const [reading, setReading] = useState<FactorsReading>(undefined);
  const [round, setRound] = useState(0);
  const reader = useRef(read);
  reader.current = read;
  useEffect(() => {
    let live = true;
    const current = reader.current;
    if (current === undefined) {
      if (import.meta.env.DEV) {
        console.warn(
          "useStepUpFactors: this site was given no reader of GET /auth/mfa, so its step-up dialog offers nothing.",
        );
      }
      return;
    }
    current()
      .then((held) => {
        if (live) setReading(held);
      })
      .catch(() => {
        if (!live) return;
        setReading({
          retry: () => {
            setReading(undefined);
            setRound((n) => n + 1);
          },
        });
      });
    return () => {
      live = false;
    };
  }, [round]);
  return read === undefined ? NO_READER : reading;
}

/**
 * A site's proof across attempts (#660, #661, #729). Each attempt spends its
 * proof — a passkey's ceremony token is single-use, and a resent one is refused
 * and counted against the lockout — so `spent()` drops it and `attempt`, used as
 * the dialog's `key`, draws a fresh dialog. `unproven` is #659's rule: no proof,
 * or an empty credential, is nothing to send.
 */
export function useStepUpAttempt() {
  const [proof, setProof] = useState<StepUpProof | null>(null);
  const [attempt, setAttempt] = useState(0);
  const spent = useCallback(() => {
    setProof(null);
    setAttempt((n) => n + 1);
  }, []);
  const unproven = proof === null || proof.credential === "";
  return { proof: unproven ? null : proof, attempt, unproven, setProof, spent };
}
